/**
 * `fetch` with a deadline. A request on a bad connection can be accepted and then never answered —
 * `fetch` alone waits forever, and the two things that wait on it (the background poller and the guest's
 * "sending" state) each let only ONE request run at a time, so one stalled request froze them for good:
 * no more checks, and "Try now" did nothing. A stalled request now ends as an ordinary network failure.
 */

/** Small requests (background checks, reading the game): a healthy answer is a few bytes and well under a second. */
export const READ_TIMEOUT_MS = 15_000;

/** Requests that carry a round or the game (photos can make them a few hundred KB): more room on a slow uplink. */
export const SEND_TIMEOUT_MS = 45_000;

/** The deadline passed with no answer — as opposed to a request that failed at once (offline, refused). */
export class RequestTimeoutError extends Error {
  constructor() {
    super('Request timed out');
    this.name = 'RequestTimeoutError';
  }
}

/**
 * The deadline covers the whole exchange, including reading the reply body — a connection that sends the
 * headers and then stalls would otherwise hang the caller's `res.json()` instead. The body is read here and
 * handed back as an ordinary `Response`, so callers work exactly as before. Rejects if the request fails or
 * the deadline passes; callers turn that into their own "couldn't reach the server" result.
 */
export async function fetchWithTimeout(url: string, init: RequestInit | undefined, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
      reject(new RequestTimeoutError());
    }, timeoutMs);
  });
  const exchange = (async (): Promise<Response> => {
    const res = await fetch(url, { ...init, signal: controller.signal });
    const text = await res.text();
    // A null-body status (204/304) cannot carry a body at all.
    return new Response(text === '' ? null : text, { status: res.status, statusText: res.statusText, headers: res.headers });
  })();
  // If the deadline wins, the exchange is aborted and rejects later — nobody is listening by then.
  exchange.catch(() => undefined);
  try {
    return await Promise.race([exchange, deadline]);
  } catch (err) {
    // The abort can surface first as an AbortError; either way it was our deadline.
    throw timedOut ? new RequestTimeoutError() : err;
  } finally {
    clearTimeout(timer);
  }
}
